import { gql } from "generated-graphql";

/**
 * Shared row/detail shape for shipments. Lines (with container + stream) and
 * the grouped vendor view ride along on every read — the backend include
 * always loads them, so field resolvers never re-query.
 */
gql(`
  fragment WasteShipmentLineItemFields on WasteShipmentLine {
    id
    quantity
    unit
    canonicalKg
    federalWasteCodes
    stateWasteCodes
    dotDescription
    profileNumber
    matchedWasteLineUnifiedId
    container {
      id
      containerLabel
      containerType
      isPermanent
      isFlowThrough
      amount
      amountUnit
      capacity
      capacityUnit
      status
    }
    wasteStream {
      id
      name
      classification
    }
  }
`);

gql(`
  fragment WasteShipmentFields on WasteShipment {
    id
    facilityId
    manifestRequestNumber
    status
    vendor {
      id
      name
    }
    transporterName
    destinationName
    destinationEpaId
    plannedShipDate
    scheduledPickupDate
    shippedDate
    requestGeneratedAt
    requestExportUrl
    backfilledFromManifest
    manifestReceivedAt
    manifestSigned
    reconciliation {
      status
      linkedManifestTrackingNumber
    }
    lines {
      ...WasteShipmentLineItemFields
    }
    manifestRequestLines {
      wasteStream {
        id
        name
        classification
      }
      containerCount
      quantity
      unit
      federalWasteCodes
      stateWasteCodes
      dotDescription
      profileNumber
    }
    totalQuantityByUnit {
      unit
      total
    }
    createdAt
    updatedAt
  }
`);

export const TENANT_WASTE_SHIPMENTS_QUERY = gql(`
  query TenantWasteShipments(
    $tenantId: ID!
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    tenant(id: $tenantId) {
      id
      wasteShipments(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
        items {
          ...WasteShipmentFields
          facility {
            id
            name
          }
        }
        count
      }
    }
  }
`);

export const FACILITY_WASTE_SHIPMENTS_QUERY = gql(`
  query FacilityWasteShipments(
    $facilityId: ID!
    $search: String
    $page: Int
    $pageSize: Int
    $sort: [SortModel!]
  ) {
    facility(id: $facilityId) {
      id
      name
      wasteShipments(search: $search, page: $page, pageSize: $pageSize, sort: $sort) {
        items {
          ...WasteShipmentFields
        }
        count
      }
    }
  }
`);

export const GET_WASTE_SHIPMENT_QUERY = gql(`
  query GetWasteShipment($id: ID!) {
    wasteShipment(id: $id) {
      ...WasteShipmentFields
      facility {
        id
        name
      }
    }
  }
`);

/**
 * The builder's container row: the shared container-list fields plus the open
 * shipments the container already sits on, so the builder can flag a
 * double-packaged container inline. Scoped to the builder (not on the shared
 * WasteContainerListItem fragment) so the containers grid doesn't pay for a
 * field it doesn't show.
 */
gql(`
  fragment ShippableContainerItem on WasteContainer {
    ...WasteContainerListItem
    openShipments {
      id
      manifestRequestNumber
      status
    }
  }
`);

/**
 * The builder's container pool: central-accumulation containers ready to
 * ship, deadline-sorted by the backend. Reuses the container list fragment
 * so the picker shows the same fields as the containers grid.
 */
export const SHIPPABLE_CONTAINERS_QUERY = gql(`
  query ShippableContainers($facilityId: ID!, $search: String, $sort: [SortModel!]) {
    facility(id: $facilityId) {
      id
      name
      shippableContainers(search: $search, sort: $sort) {
        items {
          ...ShippableContainerItem
        }
        count
      }
    }
  }
`);

export const VALIDATE_WASTE_SHIPMENT_INPUT = gql(`
  query ValidateWasteShipmentInput($input: CreateWasteShipmentInput!) {
    wasteShipmentInputValidator(input: $input) {
      ...issue
    }
  }
`);

export const CREATE_WASTE_SHIPMENT_MUTATION = gql(`
  mutation CreateWasteShipment($input: CreateWasteShipmentInput!) {
    createWasteShipment(input: $input) {
      ...WasteShipmentFields
    }
  }
`);

export const UPDATE_WASTE_SHIPMENT_MUTATION = gql(`
  mutation UpdateWasteShipment($input: UpdateWasteShipmentInput!) {
    updateWasteShipment(input: $input)
  }
`);

export const UPDATE_SHIPMENT_LINES_MUTATION = gql(`
  mutation UpdateShipmentLines($input: UpdateShipmentLinesInput!) {
    updateShipmentLines(input: $input)
  }
`);

export const CANCEL_WASTE_SHIPMENT_MUTATION = gql(`
  mutation CancelWasteShipment($id: ID!) {
    cancelWasteShipment(id: $id)
  }
`);

/**
 * Options feed for the vendor picker: the canonical vendor list narrowed to
 * vendors on the tenant's own ingested manifests — customers never see the
 * global staff-managed list.
 */
export const TENANT_WASTE_VENDOR_OPTIONS_QUERY = gql(`
  query TenantWasteVendorOptions($tenantId: ID!) {
    tenant(id: $tenantId) {
      id
      wasteVendorOptions {
        id
        name
        usedByTenant
      }
    }
  }
`);

export const VALIDATE_UPDATE_WASTE_SHIPMENT_INPUT = gql(`
  query ValidateUpdateWasteShipmentInput($input: UpdateWasteShipmentInput!) {
    wasteShipmentUpdateInputValidator(input: $input) {
      ...issue
    }
  }
`);

export const VALIDATE_UPDATE_SHIPMENT_LINES_INPUT = gql(`
  query ValidateUpdateShipmentLinesInput($input: UpdateShipmentLinesInput!) {
    updateShipmentLinesInputValidator(input: $input) {
      ...issue
    }
  }
`);

/**
 * Facilities with at least one shippable container — the only facilities the
 * builder can act on, so its picker offers exactly these (bounded list, no
 * server-search needed).
 */
export const FACILITIES_WITH_SHIPPABLE_CONTAINERS_QUERY = gql(`
  query FacilitiesWithShippableContainers($tenantId: ID!) {
    tenant(id: $tenantId) {
      id
      facilitiesWithShippableContainers {
        id
        name
      }
    }
  }
`);
